// src/features/editor/components/blocks/InfoBlockEditor.tsx
import { Label } from '@teach/ui'
import type { InfoBlock } from '@/types/blocks'
import { ImageUpload } from '@/features/editor/components/shared/ImageUpload'
import { ImagePromptField } from '@/features/editor/components/shared/ImagePromptField'
import { useOpenBlockUploadTarget } from '@/features/editor/components/shared/useOpenBlockUploadTarget'
import { useContentLang } from '@/features/editor/hooks/useContentLang'
import { getLocalizedField, setLocalizedField } from '@/features/editor/lib/localizedField'
import { readPrompt, writePrompt } from '@/features/editor/lib/imagePrompt'
import { BlockButtonsEditor } from './shared/BlockButtonsEditor'
import { HintBox } from './design/HintBox'
import { imageUploadBridge } from '@/features/editor/lib/uploadBridge'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  block: InfoBlock
  onChange: (next: InfoBlock) => void
}

export const InfoBlockEditor = ({ block, onChange }: Props) => {
  const m = useMessages(messages)
  const contentLang = useContentLang()
  const imageTarget = useOpenBlockUploadTarget((fresh, url) =>
    fresh.type === 'info' ? { ...fresh, ...setLocalizedField('image', contentLang, url) } : null,
  )

  const setImage = (url: string | null) =>
    onChange({ ...block, ...setLocalizedField('image', contentLang, url) })

  const currentImage = getLocalizedField(block, 'image', contentLang)

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-foreground font-medium">{m.image}</Label>
        {/* Цель фоновой догрузки (п.24): нужна, только если автор уйдёт, не
            дождавшись загрузки, — тогда очередь применит эту функцию к свежему
            серверному блоку. Пока панель на экране, URL как и раньше уходит в
            черновик через `onChange`. */}
        <ImageUpload
          value={currentImage}
          onChange={setImage}
          blockTarget={imageTarget}
          canvasBridge={imageUploadBridge}
        />
        {/* Как у картинки составного блока: подсказка нужна до загрузки. */}
        {currentImage ? null : <HintBox>{m.infoImageHint}</HintBox>}
        {/* Промпт живёт в теле блока: `PUT` блока — тело целиком, поэтому
            правка идёт в черновик (`onChange`), а сохраняет кнопка панели. */}
        <ImagePromptField
          value={readPrompt(block, contentLang)}
          hasImage={Boolean(currentImage)}
          onChange={(next) => onChange({ ...block, ...writePrompt(contentLang, next) })}
        />
      </div>

      <BlockButtonsEditor
        blockType={block.type}
        buttons={block.buttons}
        onChange={(buttons) => onChange({ ...block, buttons })}
      />
    </div>
  )
}
